import React from "react";
import { lazyWithDelay } from "src/utils/lazy-with-delay";

const DashboardPageComponent = lazyWithDelay(() => import("src/components/admin/dashboard"));
const ITBAdminManagementPageComponent = lazyWithDelay(() => import("src/components/admin/admin-management"));
const ListAnalysisPageComponent = lazyWithDelay(() => import("src/components/analysis/list"));
const VEAnalysisCreateUpdatePageComponent = lazyWithDelay(() => import("src/components/analysis/manage/ve/create-update"));
const VEAnalysisInputOutputLayoutPageComponent = lazyWithDelay(() => import("src/components/analysis/manage/ve/layout"));
const HomePageComponent = lazyWithDelay(() => import("src/components/users/home"));
const ClientManagementPageComponent = lazyWithDelay(() => import("src/components/admin/client-management/client"));
const CreateClientStepperPageComponent = lazyWithDelay(() => import("src/components/admin/client-management/client/create"));
const ClientTutorialsPageComponent = lazyWithDelay(() => import("src/components/users/tutorials"));
const SolutionManagementPageComponent = lazyWithDelay(() => import("src/components/admin/solution-managment"));
const UserManagementPageComponent = lazyWithDelay(() => import("src/components/admin/user-managment"));
const UserProfilePageComponent = lazyWithDelay(() => import("src/components/users/profile"));
const FAQPageComponent = lazyWithDelay(() => import("src/components/help-support/faq"));
const SupportCasesPageComponent = lazyWithDelay(() => import("src/components/help-support/support-cases"));
const ClientDocumentsManagementPageComponent = lazyWithDelay(() => import("src/components/admin/client-management/documents"));
const GuestHomePageComponent = lazyWithDelay(() => import("src/components/users/guest/home"));
const ValueEngineSharedKeyInputPageComponent = lazyWithDelay(() => import("src/components/users/guest/shared-key-input"));
const ListNotificationPageComponent = lazyWithDelay(() => import("src/components/common/notification/list-notifications"));
const CIAnalysisCreateUpdatePageComponent = lazyWithDelay(() => import("src/components/analysis/manage/ci/create-update"));
const CIAnalysisIndustryPeersInputPageComponent = lazyWithDelay(() => import("src/components/analysis/manage/ci/input/industry-peers"));
const CIAnalysisInputOutputLayoutPageComponent = lazyWithDelay(() => import("src/components/analysis/manage/ci/layout"));

export interface IRouteRoleAccess {
    route: string,
    canITBAdminAccesss: boolean,
    canClientAdminAccess: boolean,
    canSolutionAdminAccesss: boolean,
    canUserAndLightUserAccess: boolean,
    canTempUserAccess?: boolean,
    renderComponent: () => React.FunctionComponentElement<any>
}

export const AppRoute = {
    UserProfile: "/user/profile",
    Dashboard: "/dashboard",
    ManageITBAdmins: "/manage/itbadmins",
    ManageClients: "/manage/clients",
    ManageClientDocuments: "/manage/client-documents",
    AddClient: "/manage/clients/add",
    ManageSolutions: "/manage/solutions",
    ManageUsers: "/manage/users",
    ListAnalysis: "/analysis",
    VEAnalysisCreate: "/analysis/value-engine/create",
    VEAnalysisUpdate: "/analysis/value-engine/update/:analysisId",
    VEAnalysisStepper: "/analysis/value-engine/:analysisId",
    CIAnalysisCreate: "/analysis/customer-insights/create",
    CIAnalysisUpdate: "/analysis/customer-insights/update/:analysisId",
    CIAnalysisIndustryPeerInput: "/analysis/customer-insights/input/industry-peers/:analysisId",
    CIAnalysisStepper: "/analysis/customer-insights/:analysisId",
    Tutorials: "/tutorials",
    ClientHome: "/home",
    SignIn: "/signin",
    ChangePassword: "/change-password",
    ForgotPassword: "/forgot-password",
    SelfSignUp: "/signup",
    ForbiddenAccess: "/forbidden",
    SupportCases: "/support-cases",
    ContactSupport: "/support-cases/new",
    FAQ: "/faq",
    ListNotifications: "/notifications",

    GuestHome: "/guest",
    GuestSignIn: "/guest/signin",
    GuestVEAnalysisLinkShareKeyInput: "/guest/analysis/key-inputs",
    GuestVEAnalysisLinkShareWholeAnalysis: "/guest/analysis/full",
    GuestLogoutSuccess: "/guest/signed-out",
};

export const RouteRoleAccessList: IRouteRoleAccess[] = [
    {
        route: AppRoute.UserProfile,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(UserProfilePageComponent)
    },
    {
        route: AppRoute.Dashboard,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(DashboardPageComponent)
    },
    {
        route: AppRoute.ManageITBAdmins,
        canITBAdminAccesss: true,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(ITBAdminManagementPageComponent)
    },
    {
        route: AppRoute.ManageClients,
        canITBAdminAccesss: true,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(ClientManagementPageComponent)
    },
    {
        route: AppRoute.AddClient,
        canITBAdminAccesss: true,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(CreateClientStepperPageComponent)
    },
    {
        route: AppRoute.ManageSolutions,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(SolutionManagementPageComponent)
    },
    {
        route: AppRoute.ManageUsers,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(UserManagementPageComponent)
    },
    {
        route: AppRoute.ListAnalysis,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(ListAnalysisPageComponent)
    },
    {
        route: AppRoute.VEAnalysisUpdate,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(VEAnalysisCreateUpdatePageComponent)
    },
    {
        route: AppRoute.VEAnalysisCreate,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(VEAnalysisCreateUpdatePageComponent)
    },
    {
        route: AppRoute.CIAnalysisCreate,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(CIAnalysisCreateUpdatePageComponent)
    },
    {
        route: AppRoute.CIAnalysisUpdate,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(CIAnalysisCreateUpdatePageComponent)
    },
    {
        route: AppRoute.CIAnalysisIndustryPeerInput,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(CIAnalysisIndustryPeersInputPageComponent)
    },
    {
        route: AppRoute.CIAnalysisStepper,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(CIAnalysisInputOutputLayoutPageComponent)
    },
    {
        route: AppRoute.VEAnalysisStepper,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(VEAnalysisInputOutputLayoutPageComponent)
    },
    {
        route: AppRoute.Tutorials,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(ClientTutorialsPageComponent)
    },
    {
        route: AppRoute.ClientHome,
        canITBAdminAccesss: false,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(HomePageComponent)
    },
    {
        route: AppRoute.SupportCases,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(SupportCasesPageComponent)
    },
    {
        route: AppRoute.ContactSupport,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(SupportCasesPageComponent, { isGenerateNew: true })
    },
    {
        route: AppRoute.FAQ,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(FAQPageComponent)
    },
    {
        route: AppRoute.ManageClientDocuments,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: false,
        renderComponent: () => React.createElement(ClientDocumentsManagementPageComponent)
    },
    {
        route: AppRoute.GuestVEAnalysisLinkShareKeyInput,
        canITBAdminAccesss: false,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        canTempUserAccess: true,
        renderComponent: () => React.createElement(ValueEngineSharedKeyInputPageComponent)
    },
    {
        route: AppRoute.GuestVEAnalysisLinkShareWholeAnalysis,
        canITBAdminAccesss: false,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        canTempUserAccess: true,
        renderComponent: () => React.createElement(VEAnalysisInputOutputLayoutPageComponent)
    },
    {
        route: AppRoute.GuestHome,
        canITBAdminAccesss: false,
        canClientAdminAccess: false,
        canSolutionAdminAccesss: false,
        canUserAndLightUserAccess: false,
        canTempUserAccess: true,
        renderComponent: () => React.createElement(GuestHomePageComponent)
    },
    {
        route: AppRoute.ListNotifications,
        canITBAdminAccesss: true,
        canClientAdminAccess: true,
        canSolutionAdminAccesss: true,
        canUserAndLightUserAccess: true,
        renderComponent: () => React.createElement(ListNotificationPageComponent)
    },
];
